import React, { useState, useRef, useEffect } from "react";
import { Diamond, AlertCircle, CheckCircle, X } from "lucide-react";
import { getTimelinePosition } from "../../dashboard/utils/timeline";
import { dateOnlyFormatted } from "../../../utils/systemSettings";
import useNavigation from "../../../utils/useNavigation";

const KEY_EVENT_ICON_TYPES = {
  CUSTOM: 0,
  MILESTONE: 1,
  DEADLINE: 2,
  REVIEW: 3,
};

const KEY_EVENT_REF_TYPES = {
  PROGRAM: 1,
  PROJECT: 2,
};

const KEY_EVENT_ICONS = {
  [KEY_EVENT_ICON_TYPES.MILESTONE]: { icon: Diamond, color: "#9333EA" },
  [KEY_EVENT_ICON_TYPES.DEADLINE]: { icon: AlertCircle, color: "#DC2626" },
  [KEY_EVENT_ICON_TYPES.REVIEW]: { icon: CheckCircle, color: "#059669" },
};

export const TargetDateEventsRow = ({
  events = [],
  viewType,
  displayYearRange,
  dynamicWidths,
  timelineData,
  expandedProjects,
  onEventClick,
}) => {
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [clickedEvents, setClickedEvents] = useState(new Set());
  const [pinnedTooltips, setPinnedTooltips] = useState(new Map()); // Map<eventId, {position, isDragging}>
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const settings = JSON.parse(localStorage.getItem("settings"));
  const hoverTimeoutRef = useRef(null);
  const dragRefs = useRef(new Map()); // Map<eventId, dragStartData>

  const { navigateTo } = useNavigation();

  const handleRedirect = (type, redirectEntity) => {

    if (type == "program") {
      navigateTo("Programs", redirectEntity.id, redirectEntity.name);
    } else if (type == "project") {
      navigateTo("Projects", redirectEntity.id, redirectEntity.name);
    }
  };

  const handleMouseEnter = (event, e) => {
    if (pinnedTooltips.has(event.id)) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setHoveredEvent(event);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    if (hoveredEvent && pinnedTooltips.has(hoveredEvent.id)) return;

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredEvent(null);
    }, 100);
  };

  const handleTooltipMouseEnter = (event) => {
    if (pinnedTooltips.has(event.id)) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = (event) => {
    if (pinnedTooltips.has(event.id)) return;
    if (hoveredEvent?.id === event.id) {
      setHoveredEvent(null);
    }
  };

  const handleTooltipClick = (event, e) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top,
    };

    setPinnedTooltips(prev => {
      const newMap = new Map(prev);
      newMap.set(event.id, { position, isDragging: false });
      return newMap;
    });

    setHoveredEvent(null);
  };

  const handleCloseTooltip = (eventId) => {
    setPinnedTooltips(prev => {
      const newMap = new Map(prev);
      newMap.delete(eventId);
      return newMap;
    });
    dragRefs.current.delete(eventId);
  };

  const handleDragStart = (eventId, e) => {
    const pinnedData = pinnedTooltips.get(eventId);
    if (!pinnedData) return;

    dragRefs.current.set(eventId, {
      x: e.clientX,
      y: e.clientY,
      offsetX: pinnedData.position.x,
      offsetY: pinnedData.position.y,
    });

    setPinnedTooltips(prev => {
      const newMap = new Map(prev);
      newMap.set(eventId, { ...pinnedData, isDragging: true });
      return newMap;
    });
  };

  const handleDragMove = (eventId, e) => {
    const pinnedData = pinnedTooltips.get(eventId);
    const dragStart = dragRefs.current.get(eventId);

    if (!pinnedData?.isDragging || !dragStart) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    setPinnedTooltips(prev => {
      const newMap = new Map(prev);
      newMap.set(eventId, {
        ...pinnedData,
        position: {
          x: dragStart.offsetX + deltaX,
          y: dragStart.offsetY + deltaY,
        },
      });
      return newMap;
    });
  };

  const handleDragEnd = (eventId) => {
    const pinnedData = pinnedTooltips.get(eventId);
    if (!pinnedData) return;

    setPinnedTooltips(prev => {
      const newMap = new Map(prev);
      newMap.set(eventId, { ...pinnedData, isDragging: false });
      return newMap;
    });
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      pinnedTooltips.forEach((data, eventId) => {
        if (data.isDragging) {
          handleDragMove(eventId, e);
        }
      });
    };

    const handleGlobalMouseUp = () => {
      pinnedTooltips.forEach((data, eventId) => {
        if (data.isDragging) {
          handleDragEnd(eventId);
        }
      });
    };

    const hasDragging = Array.from(pinnedTooltips.values()).some(data => data.isDragging);

    if (hasDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [pinnedTooltips]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleEventClick = (event, e) => {
    e.stopPropagation();

    setClickedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(event.id)) {
        newSet.delete(event.id);
      } else {
        newSet.add(event.id);
      }

      if (onEventClick) {
        onEventClick(Array.from(newSet));
      }

      return newSet;
    });
  };

  const getRefInfo = (event) => {
    if (!event.refId || !event.refType) return null;

    for (const program of timelineData) {
      if (event.refType === KEY_EVENT_REF_TYPES.PROGRAM && program.id === event.refId) {
        return { type: 'program', name: program.name, id: program.id };
      }

      if (program.projects) {
        for (const project of program.projects) {
          if (event.refType === KEY_EVENT_REF_TYPES.PROJECT && project.id === event.refId) {
            return { type: 'project', name: project.name, id: project.id, program: { name: program.name, id: program.id } };
          }
        }
      }
    }

    return null;
  };

  // Calculate the row position for a program/project
  const getRowTopPosition = (event) => {
    if (!event.refId || !event.refType) return null;

    let topPosition = 40;

    for (let i = 0; i < timelineData.length; i++) {
      const program = timelineData[i];

      if (event.refType === KEY_EVENT_REF_TYPES.PROGRAM && event.refId === program.id) {
        return topPosition;
      }

      topPosition += 52;

      if (expandedProjects[program.id] && program.projects) {
        for (let j = 0; j < program.projects.length; j++) {
          const project = program.projects[j];

          if (event.refType === KEY_EVENT_REF_TYPES.PROJECT && event.refId === project.id) {
            return topPosition;
          }

          topPosition += 52;
        }
      }
    }

    return null;
  };

  const sortedEvents = [...events].sort((a, b) =>
    new Date(a.endDate) - new Date(b.endDate)
  );

  const renderTooltip = (event, position, isPinned) => {
    const iconType = event.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE;
    const { icon: IconComponent, color: iconColor } =
      KEY_EVENT_ICONS[iconType] || KEY_EVENT_ICONS[KEY_EVENT_ICON_TYPES.MILESTONE];
    const refInfo = getRefInfo(event);

    return (
      <div
        key={`tooltip-${event.id}`}
        className={`${isPinned ? 'fixed' : 'fixed'} z-[100]`}
        style={{
          left: `${position.x}px`,
          top: `${position.y - 10}px`,
          transform: "translate(-50%, -100%)",
          pointerEvents: isPinned ? 'auto' : 'none',
        }}
        onMouseEnter={() => handleTooltipMouseEnter(event)}
        onMouseLeave={() => handleTooltipMouseLeave(event)}
      >
        <div
          className="bg-white rounded-lg shadow-xl border border-gray-200 min-w-[220px]"
          style={{ fontFamily: "Figtree, sans-serif" }}
        >
          {isPinned && (
            <div
              className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-[#ece7fd] rounded-t-lg cursor-move"
              onMouseDown={(e) => handleDragStart(event.id, e)}
            >
              <span className="text-xs font-semibold text-gray-700">
                Key Event Details
              </span>
              <button
                onClick={() => handleCloseTooltip(event.id)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <div className="p-3">
            <div className="flex items-start gap-2 mb-2">
              <IconComponent size={14} color={iconColor} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <h4 className="font-semibold text-sm text-gray-900 flex-1">
                {event.title}
              </h4>
            </div>

            <div className="space-y-1 text-xs">
              {refInfo && (
                <div className="mb-4 pl-[25px]">
                  {refInfo.type === 'program' && (
                    <div className="flex justify-between pr-[5px]">
                      <span 
                        className="font-medium text-[#342C82] redirectable"
                        onClick={() => handleRedirect("program", refInfo)}
                      >
                        {refInfo.name}
                      </span>
                    </div>
                  )}
                  {refInfo.type === 'project' && (
                    <div className="pr-[5px]">
                      {refInfo.program && (
                        <div className="flex justify-between">
                          <span 
                            className="font-medium text-[#342C82] redirectable"
                            onClick={() => handleRedirect("program", refInfo.program)}
                          >
                            {refInfo.program.name}
                          </span>
                        </div>
                      )}
                      <div className={`flex justify-between ${refInfo.program && 'pl-[10px]'}`}>
                        <span 
                          className="font-medium text-[#342C82] redirectable"
                          onClick={() => handleRedirect("project", refInfo)}>
                          {refInfo.name}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pr-[5px] font-medium">
                <span>Date:</span>
                <span className="text-[#342C82]">
                  {dateOnlyFormatted(settings, event.endDate)}
                </span>
              </div>

              {event.customFields && event.customFields.length > 0 && (
                <div className="border-t border-gray-200 my-2 pt-2">
                  {event.customFields.map((field, index) => (
                    <div key={index} className="flex items-start gap-2 mb-1">
                      <span className="text-gray-500 w-16 flex-shrink-0">{field.name}:</span>
                      <span className="font-medium text-gray-700 flex-1">{field.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!isPinned && (
            <div
              className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
              style={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid white",
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative h-[40px] bg-[#f9f9fb] border-b-2 border-[#342c82]">
        {sortedEvents.map((event) => {
          const { left, shouldRender } = getTimelinePosition(
            event.endDate,
            event.endDate,
            viewType,
            new Date(),
            displayYearRange,
            dynamicWidths
          );

          if (!shouldRender) return null;

          const iconType = event.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE;
          const { icon: IconComponent, color: iconColor } =
            KEY_EVENT_ICONS[iconType] || KEY_EVENT_ICONS[KEY_EVENT_ICON_TYPES.MILESTONE];

          const isClicked = clickedEvents.has(event.id);
          const rowTopPosition = getRowTopPosition(event);

          return (
            <React.Fragment key={event.id}>
              <div
                className={`absolute cursor-pointer transition-all ${isClicked ? 'scale-125' : 'hover:scale-110'}`}
                style={{
                  left: `${left - 17}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: isClicked ? 20 : 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                  pointerEvents: "auto",
                }}
                onMouseEnter={(e) => handleMouseEnter(event, e)}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => {
                  handleEventClick(event, e);
                  handleTooltipClick(event, e);
                }}
              >
                <div
                  className={`rounded-full shadow-lg ${isClicked ? 'ring-4 ring-offset-2' : ''}`}
                  style={{
                    backgroundColor: iconColor,
                    padding: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    ringColor: isClicked ? `${iconColor}40` : undefined,
                  }}
                >
                  <IconComponent size={12} color="white" strokeWeight={2.5} />
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: isClicked ? 600 : 500,
                    color: iconColor,
                    fontFamily: "Figtree, sans-serif",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                    lineHeight: "1",
                    maxWidth: "80px",
                  }}
                >
                  {event.title}
                </span>
              </div>

              {/* Connection line to program/project */}
              {isClicked && rowTopPosition && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${left - 5}px`,
                    top: "40px",
                    bottom: 0,
                    width: "2px",
                    height: `${rowTopPosition - 40 + 26}px`,
                    borderLeft: `3px dashed ${iconColor}`,
                    zIndex: 15,
                    animation: "fadeIn 0.3s ease-in-out",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Render hover tooltip */}
      {hoveredEvent && !pinnedTooltips.has(hoveredEvent.id) &&
        renderTooltip(hoveredEvent, tooltipPosition, false)}

      {/* Render all pinned tooltips */}
      {Array.from(pinnedTooltips.entries()).map(([eventId, data]) => {
        const event = events.find(e => e.id === eventId);
        if (!event) return null;
        return renderTooltip(event, data.position, true);
      })}

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};
